/**
 * Service module for handling image-related API calls.
 * Provides functions to fetch images by GUID from the device API.
 */

// API configuration - using the correct vesimittari endpoint
const API_URL = "https://vesimittari.azurewebsites.net/api/rajapinta";

const defaultHeaders = {
  "Content-Type": "application/json",
};

// Image cache to avoid repeated API calls
const imageCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Cache entry structure
 * @typedef {Object} CacheEntry
 * @property {string} data - Base64 image data
 * @property {string} timestamp - Timestamp from API response (p array)
 * @property {number} cacheTimestamp - When the image was cached
 */

/**
 * Checks if a cached image is still valid
 * @param {CacheEntry} cacheEntry - The cached entry
 * @returns {boolean} Whether the cache is still valid
 */
const isCacheValid = (cacheEntry) => {
  return cacheEntry && (Date.now() - cacheEntry.cacheTimestamp) < CACHE_DURATION;
};

/**
 * Fetches an image by GUID from the device API with caching and timeout.
 * The API returns a JSON response with key 'd' containing Base64 encoded image data
 * and key 'p' containing an array with the timestamp.
 * 
 * @param {string} guid - The GUID of the image to fetch
 * @param {AbortSignal} signal - Abort signal for request cancellation
 * @returns {Promise<{imageData: string, timestamp: string}>} A promise that resolves to an object with Base64 image data and timestamp
 * @throws {Error} If the API request fails or no image data is found
 */
export const fetchImageByGuid = async (guid, signal = null) => {
  if (!guid) {
    throw new Error("GUID is required to fetch image");
  }

  // Check cache first
  const cachedEntry = imageCache.get(guid);
  if (isCacheValid(cachedEntry)) {
    return {
      imageData: cachedEntry.data,
      timestamp: cachedEntry.timestamp || null
    };
  }

  const url = API_URL;

  try {
    // Create abort controller if none provided
    const abortController = signal ? null : new AbortController();
    const requestSignal = signal || abortController.signal;

    // Set timeout
    const timeoutId = setTimeout(() => {
      if (abortController) {
        abortController.abort();
      }
    }, 30000); // 30 second timeout

    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        ...defaultHeaders,
      },
      body: JSON.stringify({ 
        c: "getimage", 
        p: [guid] 
      }),
      signal: requestSignal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Image API request failed: ${response.status}`);
    }

    // Parse the JSON response
    const text = await response.text();
    
    if (!text || text.trim() === "" || text === "undefined") {
      throw new Error("Error occurred fetching data, please contact admin");
    }
    
    let result;
    
    try {
      result = JSON.parse(text);
    } catch (parseError) {
      if (parseError.message && (parseError.message.includes("undefined") || parseError.message.includes("not valid JSON"))) {
        throw new Error("Error occurred fetching data, please contact admin");
      }
      throw new Error("Error occurred fetching data, please contact admin");
    }

    // Check if the response contains image data
    if (!result || !result.d) {
      throw new Error("No image data found in API response");
    }

    // Extract timestamp from p array (first element)
    const timestamp = result.p && result.p.length > 0 ? result.p[0] : null;

    // Cache the result
    imageCache.set(guid, {
      data: result.d,
      timestamp: timestamp,
      cacheTimestamp: Date.now()
    });

    return {
      imageData: result.d,
      timestamp: timestamp
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error("Image request timed out or was cancelled");
    }
    throw error;
  }
};

/**
 * Preloads an image to cache it for faster future access
 * @param {string} guid - The GUID of the image to preload
 * @returns {Promise<void>} A promise that resolves when preloading is complete
 */
export const preloadImage = async (guid) => {
  try {
    await fetchImageByGuid(guid);
  } catch {
    // Silently fail preloading
  }
};

/**
 * Clears the image cache
 */
export const clearImageCache = () => {
  imageCache.clear();
};

/**
 * Gets cache statistics
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  let validEntries = 0;
  let expiredEntries = 0;

  for (const entry of imageCache.values()) {
    if (isCacheValid(entry)) {
      validEntries++;
    } else {
      expiredEntries++;
    }
  }

  return {
    totalEntries: imageCache.size,
    validEntries,
    expiredEntries,
    cacheDuration: CACHE_DURATION
  };
};

/**
 * Converts Base64 image data to a data URL for display.
 * 
 * @param {string} base64Data - The Base64 encoded image data
 * @param {string} mimeType - The MIME type of the image (default: 'image/jpeg')
 * @returns {string} A data URL that can be used as src for img elements
 */
export const convertBase64ToDataUrl = (base64Data, mimeType = 'image/jpeg') => {
  if (!base64Data) {
    throw new Error("Base64 data is required");
  }
  
  return `data:${mimeType};base64,${base64Data}`;
};

